import React, { useState, useEffect, useRef } from 'react';

interface DataConnectionDialogProps {
    onImport: (data: string[][]) => void;
    onClose: () => void;
}

export const DataConnectionDialog: React.FC<DataConnectionDialogProps> = ({ onImport, onClose }) => {
    const [csvData, setCsvData] = useState('');
    const dialogRef = useRef<HTMLDivElement>(null);
    const firstInputRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        // Focus trap
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
            if (e.key === 'Tab') {
                if (!dialogRef.current) return;
                const focusableElements = dialogRef.current.querySelectorAll(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                );
                const firstElement = focusableElements[0] as HTMLElement;
                const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

                if (e.shiftKey) {
                    if (document.activeElement === firstElement) {
                        lastElement.focus();
                        e.preventDefault();
                    }
                } else {
                    if (document.activeElement === lastElement) {
                        firstElement.focus();
                        e.preventDefault();
                    }
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        // Focus first input on mount
        if (firstInputRef.current) {
            firstInputRef.current.focus();
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [onClose]);

    const handleImport = () => {
        // Simple CSV parser
        const rows = csvData.split(/\r\n|\n|\r/).map(row => row.split(',')); // Basic comma split
        // Handle quotes? For now, keep it simple.
        onImport(rows);
        onClose();
    };

    return (
        <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="import-dialog-title"
            style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'white',
                padding: '20px',
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                zIndex: 1000,
                borderRadius: '8px',
                width: '400px'
            }}
        >
            <h3 id="import-dialog-title">Import Data (CSV)</h3>
            <textarea
                ref={firstInputRef}
                value={csvData}
                onChange={(e) => setCsvData(e.target.value)}
                placeholder="Paste CSV data here..."
                aria-label="CSV Data Input"
                style={{
                    width: '100%',
                    height: '200px',
                    marginBottom: '10px',
                    fontFamily: 'monospace'
                }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button onClick={onClose}>Cancel</button>
                <button onClick={handleImport} style={{ fontWeight: 'bold' }}>Import</button>
            </div>
        </div>
    );
};
